import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGES, TRANSLATIONS } from './translations';

const STORAGE_KEY = 'izm.site.lang';
const TZ = 'Africa/Kigali';

// Browsers don't reliably ship Kinyarwanda month/day names, so they're spelled out here.
const RW_MONTHS = ['Mutarama', 'Gashyantare', 'Werurwe', 'Mata', 'Gicurasi', 'Kamena',
  'Nyakanga', 'Kanama', 'Nzeri', 'Ukwakira', 'Ugushyingo', 'Ukuboza'];
const RW_DAYS = ['Ku cyumweru', 'Ku wa mbere', 'Ku wa kabiri', 'Ku wa gatatu', 'Ku wa kane',
  'Ku wa gatanu', 'Ku wa gatandatu'];
const LOCALES = { en: 'en-GB', fr: 'fr-FR' };

function initialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (LANGUAGES.includes(saved)) return saved;
  } catch { /* storage unavailable */ }
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return LANGUAGES.includes(nav) ? nav : 'en';
}

function lookup(dict, key) {
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict);
}

/** Replaces {name} placeholders; returns a string, or an array of nodes when a param is a React element. */
function fill(template, params) {
  if (!params) return template;
  const parts = template.split(/(\{\w+\})/g);
  const hasNode = parts.some((p) => {
    const m = p.match(/^\{(\w+)\}$/);
    return m && React.isValidElement(params[m[1]]);
  });
  const mapped = parts.map((p, i) => {
    const m = p.match(/^\{(\w+)\}$/);
    if (!m || !(m[1] in params)) return p;
    const v = params[m[1]];
    return React.isValidElement(v) ? React.cloneElement(v, { key: i }) : String(v);
  });
  return hasNode ? mapped : mapped.join('');
}

/** Calendar date ("2026-11-29") → parts in the given language. */
function dateParts(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return { y, m: m - 1, d, weekday: date.getUTCDay(), date };
}

/** Instant → its date/time on the Kigali clock. */
function kigaliParts(isoInstant) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date(isoInstant)).map((x) => [x.type, x.value]));
  const iso = `${p.year}-${p.month.padStart(2, '0')}-${p.day.padStart(2, '0')}`;
  return { ...dateParts(iso), time: `${p.hour}:${p.minute}` };
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (LANGUAGES.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, params) => {
    const value = lookup(TRANSLATIONS[lang], key) ?? lookup(TRANSLATIONS.en, key) ?? key;
    return typeof value === 'string' ? fill(value, params) : value;
  }, [lang]);

  /** Full date, e.g. "Sunday, 29 November 2026" / "Ku cyumweru, tariki ya 29 Ugushyingo 2026". */
  const formatLongDate = useCallback((iso) => {
    if (!iso) return '';
    const { y, m, d, weekday, date } = dateParts(iso);
    if (lang === 'rw') return `${RW_DAYS[weekday]}, tariki ya ${d} ${RW_MONTHS[m]} ${y}`;
    return date.toLocaleDateString(LOCALES[lang], {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    });
  }, [lang]);

  /** Card date, e.g. "29 November 2026". */
  const formatDate = useCallback((iso) => {
    if (!iso) return '';
    const { y, m, d, date } = dateParts(iso);
    if (lang === 'rw') return `${d} ${RW_MONTHS[m]} ${y}`;
    return date.toLocaleDateString(LOCALES[lang], { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
  }, [lang]);

  /** Booking cut-off on the Kigali clock, e.g. "Sat 28 Nov, 00:00". */
  const formatDeadline = useCallback((isoInstant) => {
    if (!isoInstant) return '';
    if (lang === 'rw') {
      const { m, d, weekday, time } = kigaliParts(isoInstant);
      return `${RW_DAYS[weekday]} ${d} ${RW_MONTHS[m]}, ${time}`;
    }
    return new Date(isoInstant).toLocaleString(LOCALES[lang], {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: TZ,
    });
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, formatLongDate, formatDate, formatDeadline }),
    [lang, setLang, t, formatLongDate, formatDate, formatDeadline]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useI18n must be used inside LanguageProvider');
  return ctx;
}
